import { useMemo, useState } from "react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import type { UnderwritingReportKey } from "api/underwriting-agents/types";
import { IconAI } from "shared/components/ai/IconAI";
import { cx } from "shared/utils";
import { FindingCard, NeedsReviewBadge, ReviewedBadge } from "./FindingCard";
import {
  useReviewUnderwritingFindings,
  useUnderwritingFindings
} from "./useUnderwritingFindings";

/**
 * The underwriting agent's findings on the report the user is looking at; renders nothing when there are none.
 * Render it only when the application has an agent.
 */
export const ReportFindingsPanel = ({
  applicationId,
  applicantId,
  reportKey,
  onShowAllFindings
}: {
  applicationId: string;
  applicantId: number;
  reportKey: UnderwritingReportKey;
  onShowAllFindings: () => void;
}) => {
  const [expanded, setExpanded] = useState(true);
  const { data: allFindings } = useUnderwritingFindings(applicationId, {
    enabled: true
  });
  const { reviewOne, undoOne, isPending } =
    useReviewUnderwritingFindings(applicationId);

  const findings = useMemo(
    () =>
      (allFindings ?? []).filter(
        (finding) =>
          finding.applicant_id === applicantId &&
          finding.report_key === reportKey
      ),
    [allFindings, applicantId, reportKey]
  );

  if (!findings.length) return null;

  const toReview = findings.filter((finding) => !finding.reviewed_at).length;

  return (
    <section className="mb-6 overflow-hidden rounded-[10px] bg-white outline outline-1 outline-offset-0 outline-primary-500 no-print">
      <header
        className={cx(
          "flex items-center gap-2.5 px-5 py-4",
          expanded && "border-b border-alpha-40"
        )}
      >
        <span className="flex shrink-0 rounded-full shadow-[0px_0px_7px_0px_rgba(255,106,193,0.8)]">
          <IconAI bgSize={18} />
        </span>
        <p className="text-base font-bold text-content-1000">
          Underwriting agent
        </p>
        <div className="flex-1">
          <Show if={!expanded}>
            {toReview > 0 ? (
              <NeedsReviewBadge label={`${toReview} to review`} />
            ) : (
              <ReviewedBadge label="Reviewed" />
            )}
          </Show>
        </div>
        <Button
          variant={ButtonVariant.TEXT}
          size={ButtonSize.XS}
          className="!stroke-content-500 !text-content-500 hover:!stroke-content-700 hover:!text-content-700"
          onClick={onShowAllFindings}
        >
          All findings
        </Button>
        <span className="ml-1.5 flex">
          <IconButton
            HugeIcon={expanded ? ArrowUp01Icon : ArrowDown01Icon}
            variant={IconButtonVariant.BASE}
            size={IconButtonSize.XS}
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse findings" : "Expand findings"}
          />
        </span>
      </header>
      <Show if={expanded}>
        <div className="divide-y divide-alpha-40 px-5">
          {findings.map((finding) => (
            <div key={finding.id} className="py-4">
              <FindingCard
                finding={finding}
                onReview={reviewOne.mutate}
                onUndo={undoOne.mutate}
                pending={isPending(finding.id)}
              />
            </div>
          ))}
        </div>
      </Show>
    </section>
  );
};
